import React, { useEffect, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { Box, Button, Modal, Typography } from '@mui/material';

const FLAG_KEY = 'sharpstack_odds_tutorial_v1';
const MOBILE_BREAKPOINT = 900;


// ───────────────────────────────────────────────────────────────────────────
// STEPS
// ───────────────────────────────────────────────────────────────────────────

const STEPS = [
  {
    target: '.filter-section',
    title: 'Filters',
    content:
      'Filter by League, Market type (spread, moneyline, total), Period Type, and which Books to show. Toggle "Vol" to display each book\'s max bet limit under its price. The grid updates in real time as you adjust.',
    placement: 'bottom-start',
    skipBeacon: true,
  },
  {
    target: '.table-section',
    title: 'Odds Grid',
    content:
      'Every row is a game, every column is a sportsbook. Compare prices side by side. Cells with blue shading have recently updated — the deeper the blue, the fresher the price.',
    placement: 'center',
    skipBeacon: true,
  },
  {
    target: '.MuiDataGrid-columnHeader[data-field="bestOdds"]',
    title: 'Best Line Highlight',
    content:
      'The Best Odds column shows the highest available price for each line. Throughout the grid, cells with a green border show which sportsbook has the best price for that row.',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    target: '.MuiDataGrid-columnHeaders',
    title: 'Drag to Reorder',
    content:
      'Drag any sportsbook column header to reorder columns. Put your most-used books first so the best lines are easy to spot at a glance.',
    placement: 'bottom',
    skipBeacon: true,
  },
];

// ───────────────────────────────────────────────────────────────────────────
// CUSTOM TOOLTIP — full control over appearance + Step X of Y badge
// ───────────────────────────────────────────────────────────────────────────

const CustomTooltip = ({
  index,
  size,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}) => (
  <div
    {...tooltipProps}
    style={{
      backgroundColor: '#151a28',
      border: '1px solid #262d40',
      borderRadius: 12,
      padding: '20px 22px 16px',
      width: 340,
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      fontFamily: 'inherit',
    }}
  >
    <div
      style={{
        display: 'inline-block',
        background: '#4caf50',
        color: '#000',
        fontSize: 10,
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: 100,
        marginBottom: 10,
        letterSpacing: 0.5,
      }}
    >
      STEP {index + 1} OF {size}
    </div>

    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 6px 0' }}>
      {step.title}
    </h3>

    <p style={{ fontSize: 13, color: '#8892a8', lineHeight: 1.55, margin: '0 0 16px 0' }}>
      {step.content}
    </p>

    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <button
        {...skipProps}
        style={{
          background: 'none',
          border: 'none',
          color: '#666',
          fontSize: 11,
          cursor: 'pointer',
          padding: 0,
          fontFamily: 'inherit',
        }}
      >
        Skip tour
      </button>

      <div style={{ display: 'flex', gap: 6 }}>
        {index > 0 && (
          <button
            {...backProps}
            style={{
              background: 'none',
              border: '1px solid #262d40',
              color: '#8892a8',
              padding: '7px 12px',
              borderRadius: 6,
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Back
          </button>
        )}
        <button
          {...primaryProps}
          style={{
            background: '#4caf50',
            color: '#000',
            border: 'none',
            padding: '7px 16px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {isLastStep ? 'Got it ✓' : 'Next →'}
        </button>
      </div>
    </div>
  </div>
);

// ───────────────────────────────────────────────────────────────────────────
// JOYRIDE GLOBAL STYLES
// ───────────────────────────────────────────────────────────────────────────

const joyrideStyles = {
  options: {
    zIndex: 10000,
    overlayColor: 'rgba(0, 0, 0, 0.72)',
    arrowColor: '#151a28',
  },
};

// ───────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ───────────────────────────────────────────────────────────────────────────

export default function OddsTutorial() {
  const [showWelcome, setShowWelcome] = useState(false);
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    if (window.innerWidth < MOBILE_BREAKPOINT) return;
    if (localStorage.getItem(FLAG_KEY)) return;

    // Wait for the page to render before opening welcome
    const t = setTimeout(() => setShowWelcome(true), 800);
    return () => clearTimeout(t);
  }, []);

  // Listen for replay events from the sidebar
  useEffect(() => {
    const handleReplay = (e) => {
      if (e.detail?.flag !== FLAG_KEY) return;
      setRunTour(false);
      setShowWelcome(true);
    };
    window.addEventListener('sharpstack:replay-tutorial', handleReplay);
    return () => window.removeEventListener('sharpstack:replay-tutorial', handleReplay);
  }, []);

  const startTour = () => {
    setShowWelcome(false);
    // Small delay so the modal unmounts before Joyride looks for targets
    setTimeout(() => setRunTour(true), 200);
  };

  const skipTour = () => {
    setShowWelcome(false);
    localStorage.setItem(FLAG_KEY, 'true');
  };

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      localStorage.setItem(FLAG_KEY, 'true');
      setRunTour(false);
    }
  };

  return (
    <>
      {/* INTRO MODAL */}
      <Modal open={showWelcome} onClose={() => {}} disableEscapeKeyDown>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#141820',
            border: '1px solid #252a38',
            borderRadius: '16px',
            p: '44px 40px 36px',
            width: 440,
            maxWidth: '90vw',
            textAlign: 'center',
            outline: 'none',
            boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #4caf50, #2e7d32)',
              margin: '0 auto 22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
              color: '#000',
              fontWeight: 800,
            }}
          >
            S
          </Box>

          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#fff', mb: 1 }}>
            Odds Screen
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: '#94a3b8',
              lineHeight: 1.6,
              mb: 4,
              px: 1,
            }}
          >
            Compare odds across sportsbooks to find the best lines. Let's show you how.
          </Typography>

          <Button
            onClick={startTour}
            sx={{
              backgroundColor: '#4caf50',
              color: '#000',
              fontWeight: 700,
              px: 4,
              py: 1.25,
              fontSize: 14,
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': { backgroundColor: '#66bb6a' },
            }}
          >
            Show Me
          </Button>

          <Box sx={{ mt: 1.5 }}>
            <Button
              onClick={skipTour}
              sx={{
                color: '#4a5068',
                fontSize: 13,
                textTransform: 'none',
                '&:hover': { color: '#94a3b8', background: 'none' },
              }}
            >
              I've got it
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* JOYRIDE TOUR */}
      <Joyride
        steps={STEPS}
        run={runTour}
        onEvent={handleJoyrideCallback}
        continuous
        disableOverlayClose
        tooltipComponent={CustomTooltip}
        styles={joyrideStyles}
      />
    </>
  );
}